import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, GradientButton, LoadingOverlay, useAlert } from '../../components';
import { getTeamData } from '../../services/firestoreService';
import { formatCurrency, formatDate, copyToClipboard } from '../../utils/helpers';
import './ReferralScreen.css';

export const ReferralScreen = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const alert = useAlert();
    const [teamData, setTeamData] = useState({ level1: [], level2: [], level3: [] });
    const [loading, setLoading] = useState(true);
    const [activeLevel, setActiveLevel] = useState('A');
    const [selectedMember, setSelectedMember] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        loadTeamData();
    }, []);

    const loadTeamData = async () => {
        if (!userData?.uid) return;

        try {
            const data = await getTeamData(userData.uid, userData.referralCode);
            setTeamData(data);
        } catch (error) {
            console.error('Error loading team data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCode = async () => {
        if (userData?.referralCode) {
            const success = await copyToClipboard(userData.referralCode);
            if (success) {
                alert.showSuccess('Copied', 'Referral code copied to clipboard');
            }
        }
    };

    const handleCopyLink = async () => {
        const link = `${window.location.origin}/signup?ref=${userData?.referralCode}`;
        const success = await copyToClipboard(link);
        if (success) {
            alert.showSuccess('Copied', 'Referral link copied to clipboard');
        }
    };

    const handleShare = async () => {
        const text = `Join Yonex AI and start earning! Use my referral code: ${userData?.referralCode}\n\nSign up now: ${window.location.origin}/signup?ref=${userData?.referralCode}`;

        if (navigator.share) {
            try {
                await navigator.share({ text });
            } catch (error) {
                handleCopyLink();
            }
        } else {
            handleCopyLink();
        }
    };

    const levels = [
        { key: 'A', label: 'Level A', data: teamData.level1 },
        { key: 'B', label: 'Level B', data: teamData.level2 },
        { key: 'C', label: 'Level C', data: teamData.level3 },
    ];

    const totalMembers = teamData.level1.length + teamData.level2.length + teamData.level3.length;

    if (loading) {
        return <LoadingOverlay visible message="Loading team data..." />;
    }

    return (
        <div className="referral-screen">
            <div className="screen-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="screen-title">My Team</h1>
                <div style={{ width: 40 }}></div>
            </div>

            {/* Referral Code Card */}
            <GlassCard className="referral-card">
                <h3 className="referral-title">Your Referral Code</h3>
                <div className="referral-code-box">
                    <span className="referral-code">{userData?.referralCode || 'N/A'}</span>
                    <button className="copy-btn" onClick={handleCopyCode}>
                        <span className="material-icons">content_copy</span>
                    </button>
                </div>
                <div className="share-buttons">
                    <GradientButton
                        title="Copy Link"
                        variant="outline"
                        size="small"
                        onClick={handleCopyLink}
                        icon={<span className="material-icons">link</span>}
                    />
                    <GradientButton
                        title="Share"
                        size="small"
                        onClick={handleShare}
                        icon={<span className="material-icons">share</span>}
                    />
                </div>
            </GlassCard>

            {/* Earnings & Total Stats */}
            <div className="stats-row highlight">
                <GlassCard className="stat-card earning">
                    <span className="stat-value primary">{formatCurrency(userData?.totalTeamEarnings || 0)}</span>
                    <span className="stat-label">Team Earnings</span>
                </GlassCard>
                <GlassCard className="stat-card">
                    <span className="stat-value">{totalMembers}</span>
                    <span className="stat-label">Total Team</span>
                </GlassCard>
            </div>

            {/* Level Stats */}
            <div className="stats-row secondary">
                <GlassCard className="stat-card">
                    <div className="stat-header">
                        <span className="stat-value">{teamData.level1.length}</span>
                        <span className="commission-rate">8%</span>
                    </div>
                    <span className="stat-label">Level A</span>
                </GlassCard>
                <GlassCard className="stat-card">
                    <div className="stat-header">
                        <span className="stat-value">{teamData.level2.length}</span>
                        <span className="commission-rate">3%</span>
                    </div>
                    <span className="stat-label">Level B</span>
                </GlassCard>
                <GlassCard className="stat-card">
                    <div className="stat-header">
                        <span className="stat-value">{teamData.level3.length}</span>
                        <span className="commission-rate">1%</span>
                    </div>
                    <span className="stat-label">Level C</span>
                </GlassCard>
            </div>

            {/* Level Tabs */}
            <div className="level-tabs">
                {levels.map((level) => (
                    <button
                        key={level.key}
                        className={`level-tab ${activeLevel === level.key ? 'active' : ''}`}
                        onClick={() => setActiveLevel(level.key)}
                    >
                        {level.label} ({level.data.length})
                    </button>
                ))}
            </div>

            {/* Team List */}
            <div className="team-list">
                {levels.find(l => l.key === activeLevel)?.data.length > 0 ? (
                    levels.find(l => l.key === activeLevel)?.data.map((member) => (
                        <GlassCard
                            key={member.uid}
                            className="member-card"
                            onClick={() => {
                                setSelectedMember(member);
                                setShowModal(true);
                            }}
                        >
                            <div className="member-avatar">
                                <span>{(member.phoneNumber || member.email || 'U')[0].toUpperCase()}</span>
                            </div>
                            <div className="member-info">
                                <span className="member-email">{member.phoneNumber || member.email}</span>
                                <div className="member-meta">
                                    <span className="member-date">{formatDate(member.createdAt)}</span>
                                    {member.totalRecharged > 0 && (
                                        <span className="member-badge">Active</span>
                                    )}
                                </div>
                            </div>
                            <span className="material-icons member-more">chevron_right</span>
                        </GlassCard>
                    ))
                ) : (
                    <div className="empty-level">
                        <span className="material-icons">group_off</span>
                        <p>No members in this level yet</p>
                    </div>
                )}
            </div>

            {/* Member Details Modal */}
            {showModal && selectedMember && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <GlassCard className="member-details-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-title-group">
                                <div className="modal-avatar">
                                    <span>{(selectedMember.phoneNumber || selectedMember.email || 'U')[0].toUpperCase()}</span>
                                </div>
                                <div className="modal-title-info">
                                    <h3>Team Member</h3>
                                    <p>{selectedMember.phoneNumber || selectedMember.email}</p>
                                </div>
                            </div>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <span className="material-icons">close</span>
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="detail-row">
                                <div className="detail-item">
                                    <span className="detail-label">Joined Date</span>
                                    <span className="detail-value">{formatDate(selectedMember.createdAt)}</span>
                                </div>
                                <div className="detail-item text-right">
                                    <span className="detail-label">Member Level</span>
                                    <span className="detail-value">Level {activeLevel}</span>
                                </div>
                            </div>

                            <div className="detail-card-row">
                                <div className="info-glass-card">
                                    <div className="info-icon phone-bg">
                                        <span className="material-icons">phone</span>
                                    </div>
                                    <div className="info-content">
                                        <span className="info-label">Mobile Number</span>
                                        <span className="info-value">{selectedMember.phoneNumber || 'Not Added'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-card-row">
                                <div className="info-glass-card">
                                    <div className="info-icon recharge-bg">
                                        <span className="material-icons">payments</span>
                                    </div>
                                    <div className="info-content">
                                        <span className="info-label">Total Recharge</span>
                                        <span className="info-value primary">{formatCurrency(selectedMember.totalRecharged || 0)}</span>
                                    </div>
                                </div>
                            </div>

                            <GradientButton
                                title="Close"
                                variant="outline"
                                onClick={() => setShowModal(false)}
                                className="modal-close-btn"
                            />
                        </div>
                    </GlassCard>
                </div>
            )}
        </div>
    );
};

export default ReferralScreen;
